import { useCallback, useEffect, useMemo, useState } from "react";
import { Container } from "../styles";
import HelmetUTP from "components/HelmetUTP/HelmetUTP";
import BackToView from "components/BackToView/BackToView";
import CustomButton from "components/CustomButton/CustomButton";
import Preloader from "components/Preloader/Preloader";
import ToastMessage from "components/ToastMessage/ToastMessage";
import DataHeaderDetail from "components/DataHeaderDetail/DataHeaderDetail";
import EmptyData from "views/components/EmptyData/EmptyData";
import DialogAlertAction from "components/DialogAlertAction/DialogAlertAction";
import { Row } from "components/styles";
import EventIcon from "icons/EventIcon";
import AddingPersonIcon from "icons/AddingPersonIcon";
import ABSOLUTE_PATHS from "routes/utils/absolutePaths";
import useLoadingRequest from "components/Preloader/utils/hooks/useLoadingRequest";
import usePaginator from "components/Paginator/utils/hooks/usePaginator";
import useToastMessage from "components/ToastMessage/utils/hooks/useToastMessage";
import { requestGetAllCampus } from "services/general";
import {
	computeEventSignatureFileChecksum,
	requestCheckEventSignatureAdminFilePersistence,
	requestCompensateEventSignatureAdminFile,
	requestCreateEventSignatureAdmin,
	requestCreateEventSignatureAdminUploadContext,
	requestGetEventSignatureAdminFileUrl,
	requestGetEventSignatureAdminList,
	requestGetEventSignatureAdminTypes,
	requestToggleEventSignatureAdmin,
	requestUpdateEventSignatureAdminUploadContext,
	requestUpdateEventSignatureAdmin,
	uploadEventSignatureFile,
} from "services/settings/eventSignatureAdmin/aboutEventSignatureAdmin";
import Header from "./components/Header/Header";
import EventSignatureListTable from "./components/EventSignatureListTable/EventSignatureListTable";
import EventSignatureFormDialog from "./components/EventSignatureFormDialog/EventSignatureFormDialog";
import {
	INITIAL_FILTER_FIELDS,
	RECORDS_BY_PAGE,
} from "./utils/constants";
import { normalizeSignaturePayload } from "./utils/helpers";
import {
	PageSection,
	TableCard,
	TableSection,
	PreviewContent,
	PreviewFrame,
	PreviewHeader,
} from "./styles";

const SettingsEventSignatureAdmin = () => {
	const loadingRequest = useLoadingRequest();
	const toastMessage = useToastMessage();
	const { value: loadingRequestValue, show: showLoading, hide: hideLoading } =
		loadingRequest;
	const { value: toastMessageValue, show: showToast } = toastMessage;
	const [allCampus, setAllCampus] = useState([]);
	const [signatureTypes, setSignatureTypes] = useState([]);
	const [signatures, setSignatures] = useState([]);
	const paginator = usePaginator();
	const [filterFields, setFilterFields] = useState({
		...INITIAL_FILTER_FIELDS,
	});
	const [showForm, setShowForm] = useState(false);
	const [currentItem, setCurrentItem] = useState(null);
	const [itemForToggle, setItemForToggle] = useState(null);
	const [fatalError, setFatalError] = useState(false);
	const [previewFile, setPreviewFile] = useState(null);
	const [formSubmitError, setFormSubmitError] = useState("");

	const buildListRequestFilters = (filters, page = 1) => ({
		campusId: filters.campusId,
		signatureTypeCode: filters.signatureTypeCode,
		status: filters.status,
		validity: filters.validity,
		search: filters.search,
		page,
		pageSize: RECORDS_BY_PAGE,
	});

	const campusOptions = useMemo(
		() => [
			{ value: 0, text: "Todos" },
			...allCampus.map(({ id, name }) => ({
				value: id,
				text: name,
			})),
		],
		[allCampus]
	);
	const signatureTypeOptions = useMemo(
		() =>
			(signatureTypes || []).map(({ code, name }) => ({
				value: code,
				text: name,
			})),
		[signatureTypes]
	);

	const showErrorToast = useCallback(
		message =>
			showToast(
				message || "No pudimos completar la operación del administrador de firmas.",
				"error"
			),
		[showToast]
	);

	const loadSignaturePage = async ({
		filters = filterFields,
		page = 1,
		loadingMessage = "Cargando administrador de firmas...",
	} = {}) => {
		showLoading(loadingMessage);
		try {
			const response = await requestGetEventSignatureAdminList(
				buildListRequestFilters(filters, page)
			);
			const items = response?.items || [];
			const total = Number(response?.total) || 0;
			const resolvedPage = Number(response?.page) || page;
			const resolvedPageSize =
				Number(response?.pageSize) || RECORDS_BY_PAGE;
			setSignatures(items);
			paginator.setQuantityPages({
				quantityPages: Math.max(1, Math.ceil(total / resolvedPageSize)),
				totalRecords: total,
			});
			paginator.movePage(resolvedPage);
			setFatalError(false);
			return response;
		} catch (error) {
			showErrorToast(error?.message);
			setFatalError(true);
			return null;
		} finally {
			hideLoading();
		}
	};

	const fillInitialData = useCallback(async () => {
		showLoading("Cargando administrador de firmas...");
		try {
			const [campus, types] = await Promise.all([
				requestGetAllCampus(),
				requestGetEventSignatureAdminTypes(),
			]);
			setAllCampus(campus || []);
			setSignatureTypes(types || []);
			const response = await requestGetEventSignatureAdminList(
				buildListRequestFilters(INITIAL_FILTER_FIELDS, 1)
			);
			const items = response?.items || [];
			const total = Number(response?.total) || 0;
			const resolvedPage = Number(response?.page) || 1;
			const resolvedPageSize =
				Number(response?.pageSize) || RECORDS_BY_PAGE;
			setSignatures(items);
			paginator.setQuantityPages({
				quantityPages: Math.max(1, Math.ceil(total / resolvedPageSize)),
				totalRecords: total,
			});
			paginator.movePage(resolvedPage);
			setFatalError(false);
		} catch (error) {
			showErrorToast(error?.message);
			setFatalError(true);
		} finally {
			hideLoading();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hideLoading, showErrorToast, showLoading]);

	const applyFilters = async (filters = filterFields) =>
		loadSignaturePage({
			filters,
			page: 1,
			loadingMessage: "Filtrando firmas...",
		});

	useEffect(() => {
		fillInitialData();
	}, [fillInitialData]);

	const filters = useMemo(
		() => ({
			value: filterFields,
			change: (field, value) =>
				setFilterFields(prev => ({
					...prev,
					[field]: value,
				})),
			clear: () => {
				const clearedFilters = { ...INITIAL_FILTER_FIELDS };
				setFilterFields(clearedFilters);
				return clearedFilters;
			},
			isCleared: () =>
				Object.keys(filterFields).every(
					key => filterFields[key] === INITIAL_FILTER_FIELDS[key]
				),
		}),
		[filterFields]
	);

	const openForm = item => {
		setFormSubmitError("");
		setCurrentItem(item || null);
		setShowForm(true);
	};

	const closeForm = () => {
		setFormSubmitError("");
		setCurrentItem(null);
		setShowForm(false);
	};

	const buildSubmissionPayload = useCallback(
		async form => {
			const payload = normalizeSignaturePayload(form);
			const rawFile = form?.signatureFile?.rawFile || null;
			if (!rawFile) {
				return {
					payload: currentItem
						? {
								...payload,
								signatureFile: {
									name: "",
									contentType: "",
									path: "",
									bucketName: "",
									checksum: "",
									base64: "",
								},
						  }
						: payload,
					fileUploaded: false,
				};
			}

			const uploadContext = currentItem
				? await requestUpdateEventSignatureAdminUploadContext(
						currentItem.id,
						payload
				  )
				: await requestCreateEventSignatureAdminUploadContext(payload);
			const checksum = await computeEventSignatureFileChecksum(rawFile);
			await uploadEventSignatureFile({
				uploadUrl: uploadContext.signatureFile.uploadUrl,
				file: rawFile,
				contentType: uploadContext.signatureFile.contentType,
			});
			const previousSignatureFile =
				currentItem?.signatureFile?.path &&
				currentItem?.signatureFile?.bucketName &&
				currentItem.signatureFile.path !== uploadContext.signatureFile.path
					? {
							name: currentItem.signatureFile.name,
							contentType: currentItem.signatureFile.contentType,
							path: currentItem.signatureFile.path,
							bucketName: currentItem.signatureFile.bucketName,
							checksum: currentItem.signatureFile.checksum,
					  }
					: null;
			return {
				payload: {
					...payload,
					signatureFile: {
						...uploadContext.signatureFile,
						checksum,
					},
				},
				fileUploaded: true,
				uploadedSignatureFile: {
					...uploadContext.signatureFile,
					checksum,
				},
				previousSignatureFile,
			};
		},
		[currentItem]
	);

	const submitForm = async form => {
		setFormSubmitError("");
		showLoading(
			currentItem ? "Guardando firma..." : "Registrando firma..."
		);
		let fileUploaded = false;
		let uploadedSignatureFile = null;
		let previousSignatureFile = null;
		try {
			const submission = await buildSubmissionPayload(form);
			const payload = submission.payload;
			fileUploaded = submission.fileUploaded;
			uploadedSignatureFile = submission.uploadedSignatureFile || null;
			previousSignatureFile = submission.previousSignatureFile || null;
			if (currentItem)
				await requestUpdateEventSignatureAdmin(currentItem.id, payload);
			else await requestCreateEventSignatureAdmin(payload);
			if (previousSignatureFile) {
				try {
					await requestCompensateEventSignatureAdminFile(previousSignatureFile);
				} catch (cleanupError) {
					showToast(
						"La firma se guardó correctamente, pero no pudimos limpiar el archivo anterior."
					);
				}
			}
			closeForm();
			await applyFilters();
			showToast(
				currentItem
					? "La firma se actualizó correctamente."
					: "La firma se registró correctamente."
			);
		} catch (error) {
			if (uploadedSignatureFile?.path && uploadedSignatureFile?.bucketName) {
				try {
					const persistenceCheck =
						await requestCheckEventSignatureAdminFilePersistence(
							uploadedSignatureFile
						);
					if (persistenceCheck.persisted) {
						if (previousSignatureFile) {
							try {
								await requestCompensateEventSignatureAdminFile(
									previousSignatureFile
								);
							} catch (cleanupError) {
								showToast(
									"La firma sí se guardó, pero no pudimos limpiar el archivo anterior."
								);
							}
						}
						closeForm();
						await applyFilters();
						showToast(
							currentItem
								? "La firma se actualizó correctamente. La respuesta final no llegó, pero validamos que el registro sí se guardó."
								: "La firma se registró correctamente. La respuesta final no llegó, pero validamos que el registro sí se guardó."
						);
						return;
					}

					await requestCompensateEventSignatureAdminFile(uploadedSignatureFile);
				} catch (cleanupError) {
					setFormSubmitError(
						`${
							fileUploaded
								? error?.message ||
								  "El archivo se cargó, pero no pudimos registrar la firma."
								: error?.message ||
								  "No pudimos completar la operación del administrador de firmas."
						} Tampoco pudimos revertir el archivo subido; puede haber quedado un archivo pendiente de limpieza.`
					);
					return;
				}
			}
			setFormSubmitError(
				fileUploaded
					? error?.message ||
						"El archivo se cargó, pero no pudimos registrar la firma. Reintenta la operación."
					: error?.message
			);
		} finally {
			hideLoading();
		}
	};

	const openToggleDialog = item => {
		setItemForToggle(item);
	};

	const openSignatureFile = useCallback(
		async item => {
			if (!item?.id || !item?.signatureFile?.path) {
				showErrorToast("La firma seleccionada no tiene un archivo disponible.");
				return;
			}

			showLoading("Abriendo archivo de firma...");
			try {
				const signatureFile = await requestGetEventSignatureAdminFileUrl(item.id);
				if (!signatureFile?.url) {
					throw new Error("No pudimos obtener la URL del archivo de firma.");
				}
				setPreviewFile({
					name: signatureFile.name || item.signatureFile?.name || "Firma",
					url: signatureFile.url,
					contentType: signatureFile.contentType || item.signatureFile?.contentType || "image/png",
				});
			} catch (error) {
				showErrorToast(error?.message);
			} finally {
				hideLoading();
			}
		},
		[hideLoading, showErrorToast, showLoading]
	);

	const closePreview = () => {
		setPreviewFile(null);
	};

	const toggleActiveState = async () => {
		if (!itemForToggle) return;
		showLoading(
			itemForToggle.isActive
				? "Desactivando firma..."
				: "Activando firma..."
		);
		try {
			await requestToggleEventSignatureAdmin(
				itemForToggle.id,
				!itemForToggle.isActive
			);
			setItemForToggle(null);
			await applyFilters();
			showToast(
				itemForToggle.isActive
					? "La firma se desactivó correctamente."
					: "La firma se activó correctamente."
			);
		} catch (error) {
			showErrorToast(error?.message);
		} finally {
			hideLoading();
		}
	};

	const hasActiveSearch = Boolean((filterFields.search || "").trim());

	const changePage = async page => {
		await loadSignaturePage({
			filters: filterFields,
			page,
			loadingMessage: "Cargando firmas...",
		});
	};

	return (
		<>
			<HelmetUTP title="Admin. firmas eventos" />
			<ToastMessage {...toastMessageValue} />
			<Preloader {...loadingRequestValue} />
			<BackToView
				to={ABSOLUTE_PATHS.SETTINGS}
				text="Volver a Configuración"
			/>
			<Container>
				<PageSection>
				<Row>
					<DataHeaderDetail
						icon={<EventIcon />}
						text="Administrador de Firmas Eventos"
						width="320px"
					/>
					<CustomButton
						icon={<AddingPersonIcon />}
						text="Registrar firma"
						size="medium"
						onClick={() => openForm(null)}
					/>
				</Row>
				<Header
					filters={filters}
					campusOptions={campusOptions}
					signatureTypeOptions={signatureTypeOptions}
					applyFilters={() => applyFilters()}
					paginator={paginator}
					changePage={changePage}
				/>
				{fatalError ? (
					<EmptyData
						content={
							<>
								No pudimos cargar el administrador de firmas de eventos.
								Reintenta la carga para continuar.
							</>
						}
					/>
				) : paginator.data.totalRecords === 0 ? (
					<EmptyData
						content={
							hasActiveSearch ? (
								<>
									No se encontraron firmas que coincidan con{" "}
									<b>"{filterFields.search}"</b>. Intenta con otro término.
								</>
							) : (
								<>
									Actualmente <b>no existen firmas registradas</b> con los
									filtros aplicados.
								</>
							)
						}
					/>
				) : (
					<TableSection>
						<TableCard>
							<EventSignatureListTable
								list={signatures}
								allCampus={allCampus}
								openForm={openForm}
								openFile={openSignatureFile}
								openToggleDialog={openToggleDialog}
							/>
						</TableCard>
					</TableSection>
				)}
				</PageSection>
			</Container>
			<EventSignatureFormDialog
				show={showForm}
				toggleShow={value => {
					if (value === false) closeForm();
				}}
				currentItem={currentItem}
				allCampus={allCampus}
				signatureTypeOptions={signatureTypeOptions}
				onSubmit={submitForm}
				onPreviewFile={openSignatureFile}
				submitError={formSubmitError}
			/>
			<DialogAlertAction
				show={Boolean(itemForToggle)}
				toggleShow={() => setItemForToggle(null)}
				title={
					itemForToggle?.isActive
						? "¿Deseas desactivar esta firma?"
						: "¿Deseas activar esta firma?"
				}
				contentComponent={
					<div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
						<p className="paragraph center">
							{itemForToggle?.isActive
								? "La firma seguirá visible para edición, pero quedará inactiva para su uso posterior."
								: "La firma volverá a quedar disponible para su uso posterior."}
						</p>
					</div>
				}
			>
				<CustomButton
					variant="secondary"
					size="medium"
					text="Cancelar"
					onClick={() => setItemForToggle(null)}
				/>
				<CustomButton
					size="medium"
					text={itemForToggle?.isActive ? "Desactivar" : "Activar"}
					onClick={toggleActiveState}
				/>
			</DialogAlertAction>
			<DialogAlertAction
				show={Boolean(previewFile)}
				toggleShow={closePreview}
				noImage
				title="Vista previa de firma"
				contentComponent={
					previewFile ? (
						<PreviewContent>
							<PreviewHeader>
								<h4>{previewFile.name}</h4>
								<p>
									Revisa la firma sin salir del administrador. Si necesitas más
									espacio, también puedes abrirla en una pestaña aparte.
								</p>
							</PreviewHeader>
							<PreviewFrame>
								<img src={previewFile.url} alt={previewFile.name} />
							</PreviewFrame>
						</PreviewContent>
					) : null
				}
			>
				<CustomButton
					variant="secondary"
					size="medium"
					text="Cerrar"
					onClick={closePreview}
				/>
				<CustomButton
					size="medium"
					text="Abrir en pestaña"
					onClick={() => {
						if (!previewFile?.url) return;
						const previewTab = window.open(previewFile.url, "_blank");
						if (previewTab) previewTab.opener = null;
					}}
				/>
			</DialogAlertAction>
		</>
	);
};

export default SettingsEventSignatureAdmin;
